import React, { useEffect } from 'react';
import { AdacHeadline } from '@adac-de/component-library';
import { useFormContext, useWatch } from 'react-hook-form';
import { logger } from '@adac-de/e-charge-common';
import offerScreenStyles from '../../sections/result/screen/offer-screen.module.scss';
import offerFormStyles from './offer-form.module.scss';
import { FormButtonWrapper } from './inputs/form-button-wrapper';
import { PartnerOfferingForm } from '@components/offer-form/offer-form-second';
import { UnderlinedButton } from '@components/underlined-button/underlined-button';
import { replaceContentfulPlaceholder } from '@utils/replace-contentful-placeholder';
import { BenefitsBox } from '@components/benefits-box/benefits-box';
import { PartnerBoxIntro } from '@components/offer-form/partner-box-intro';
import { PartnerSelection } from '@components/offer-form/form-steps/partner-selection';
import { Button } from '@components/button/button';
import { isPartners } from 'types';

type OfferScreenFirstPageProps = {
  headline: PvWizardSectionOffer['headline'];
  toggleDisclaimerModal: () => void;
  partners: PvWizardSectionOffer['partners'];
  result: PvWizardResultSectionOffer;
  benefits: PvWizardSectionOffer['benefits'];
  hasVehicle: boolean;
  navigateToPage: (page: number) => void;
  scrollIntoView: () => void;
};

export const OfferFormFirst: React.FC<OfferScreenFirstPageProps> = ({
  headline,
  toggleDisclaimerModal,
  partners,
  result,
  benefits,
  hasVehicle,
  navigateToPage,
  scrollIntoView
}) => {
  const valueHeadline = replaceContentfulPlaceholder({
    content: headline,
    values: result.values
  });

  const { control, setError, clearErrors } = useFormContext<PartnerOfferingForm>();

  const partnerNames = partners.boxes.map(box => box.partner);

  const selectedPartners = useWatch<PartnerOfferingForm, 'partners'>({
    control,
    name: 'partners'
  });

  useEffect(() => {
    if (selectedPartners && selectedPartners.length > 0) {
      clearErrors('partners');
    }
  }, [selectedPartners, clearErrors]);

  const handleNextButtonClick = () => {
    if (selectedPartners.length === 0) {
      setError('partners', { type: 'manual' });
      return;
    }
    navigateToPage(2);
    scrollIntoView();
  };

  if (!isPartners(partnerNames)) {
    logger.error('Invalid partner names in partners');
    return;
  }

  return (
    <>
      <AdacHeadline tagName="h1" styleAs="h1" className="margin-bottom--s">
        {valueHeadline}
        <UnderlinedButton
          label=""
          onClick={toggleDisclaimerModal}
          withInfoIcon
          iconSize={24}
          iconFillColor="black60"
          className={offerScreenStyles.infoIcon}
        />
      </AdacHeadline>
      <div className={offerFormStyles.offerForm}>
        <div className={offerFormStyles.offerFormSection}>
          <PartnerBoxIntro partners={partners} />
          <PartnerSelection partnerNames={partnerNames} partnerBoxes={partners.boxes} />
        </div>
        <FormButtonWrapper>
          <Button
            type="button"
            onClick={handleNextButtonClick}
            tracking={{
              click: {
                label: 'Jetzt Angebot anfordern',
                element: 'Partnerauswahl',
                funnelStep: 13
              }
            }}
          >
            Jetzt Angebot anfordern
          </Button>
        </FormButtonWrapper>
      </div>
      {benefits?.headline && benefits.listItems && result && (
        <BenefitsBox
          benefits={{
            headline: benefits.headline,
            listItem: benefits.listItems,
            image: benefits.image
          }}
          hasVehicle={hasVehicle}
          roundedValues={result.values}
        />
      )}
    </>
  );
};
